import * as Tone from 'tone';
import { gsap, Power2 } from "gsap";
import { AppEvent, AudioStep } from "@/constants";
import { AudioComponent } from "./AudioComponent";
import { GroupPlayer } from "../players/GroupPlayer";
import { AudioScene, BackgroundPlayer, GesturePlayer } from "@audio/index";
const AudioSource = {
    ONE: 0,
    TWO: 1,
};
export class AudioWorld extends AudioComponent {
    constructor({ name, audioData }) {
        super(name);
        this._crossFadeIndex = 0;
        this._loadQueue = [];
        this._sceneIndex = AudioStep.INIT;
        this._previousSceneIndex = AudioStep.INIT;
        this._voiceoverPlaying = false;
        this.scenes = [];
        this.sharedLoopsPlayers = new Map();
        this.debug = true;
        this.config = audioData;
        // Mix scene audio transitions nicely
        this.output = new Tone.Gain(0).toDestination();
        this.crossFade = new Tone.CrossFade(AudioSource.ONE).connect(this.output);
        // So the loops run correctly, adjust time signature now
        this.resetGlobals();
        // All the players we need to run this world
        this.createPlayers();
    }
    /**
     * Create the players for this world
     * - GesturePlayer - plays the gesture audio
     * - BackgroundPlayer - plays the background audio
     * - LoopPlayer - plays world-level synced loops
     */
    createPlayers() {
        const { background, gesture, sharedLoops, scenes } = this.config;
        // Maybe has gesture audio
        if (gesture) {
            this.gesturePlayer = new GesturePlayer({
                input: this.output,
                path: this.filePath,
                config: gesture,
            });
        }
        // Maybe a background player
        if (background) {
            this.bgPlayer = new BackgroundPlayer({
                name: `--[ ${this.name}: shared background ]`,
                input: this.output,
                path: this.filePath,
                config: background,
            }).connect(this.output);
        }
        console.log("Shared loops: ", sharedLoops);
        // Analyse the scenes to work out how many players we need
        if (!sharedLoops) {
            return;
        }
        // Find which groups are needed to run the first 2 scenes
        // Might not be any loops in the first (or second) scene
        const preloadGroups = [
            ...new Map(scenes.slice(0, 2)
                .filter(scene => !!scene.tracks)
                .map(scene => scene.tracks.map(track => track.group))
                .flat()
                .map(group => [group, group])) // Why did I do this?
                .values()
        ];
        console.log("Preload groups", preloadGroups);
        sharedLoops.forEach(groupConfig => {
            // Whether to autoload (we'll load the first two scenes' groups)
            const autoLoad = preloadGroups.includes(groupConfig.name);
            // We only want to autoload if necessary
            const groupPlayer = new GroupPlayer(Object.assign(Object.assign({}, groupConfig), { input: this.output, path: this.filePath }));
            // If required, create the sampler and load now
            if (autoLoad) {
                const sampler = groupPlayer.createSampler();
                sampler.connect(this.output);
                this.addToLoadQueue(groupPlayer, groupConfig.name);
            }
            this.sharedLoopsPlayers.set(groupConfig.name, groupPlayer);
        });
    }
    /**
     *  Enter the world, optionally at a given scene
     * @param param0
     *    initialScene - the scene to start at
     */
    enter({ initialScene = AudioStep.TITLES, skipVoiceover = false }) {
        this.resetGlobals();
        this._playRequestedAt = Tone.now();
        this.selectScene(initialScene);
        // TODO: Also check user prefs
        if (this.config.voiceover && !skipVoiceover) {
            this.playVoiceover();
        }
    }
    /**
     * If there is a voiceover, play it now
     */
    playVoiceover() {
        const { id } = this.config.voiceover;
        const url = `./audio/${this.filePath}/${id}.mp3`;
        clearTimeout(this.voTimeoutId);
        const startLoadTimeStamp = Tone.now();
        this.voiceoverPlayer = new Tone.Player({
            url,
            volume: -6,
            autostart: false,
            onload: () => {
                // Fade other stuff out
                const now = Tone.now();
                const delay = Math.max(0, 2000 - (now - startLoadTimeStamp)); // 2 second delay - account for loading time
                this.voTimeoutId = setTimeout(() => {
                    this.voiceoverPlayer.start("+0.5");
                    this.output.gain.rampTo(0.425, 1);
                }, delay);
            },
            onstop: () => {
                this.voiceoverPlayer.disconnect();
                this.voiceoverPlayer.dispose();
                // fade back up
                this.output.gain.rampTo(0.85, 2);
            },
        }).toDestination();
        this._voiceoverPlaying = true;
    }
    stopVoiceover() {
        if (this._voiceoverPlaying) {
            this.voiceoverPlayer.disconnect();
            this.voiceoverPlayer.dispose();
            this._voiceoverPlaying = false;
            this.output.gain.rampTo(0.85, 2);
        }
    }
    /**
     * Select a scene by index
     * @param sceneIndex
     */
    selectScene(sceneIndex) {
        // console.log(`AudioWorld ${this.name} – selecting scene ${this.config.scenes[sceneIndex].name}`);
        var _a;
        // Update the current value
        this._previousSceneIndex = this._sceneIndex;
        this._sceneIndex = sceneIndex;
        // Kill the previous one if necessary - could also be ahead
        if (this.config.scenes.length > 1) {
            (_a = this.previousScene) === null || _a === void 0 ? void 0 : _a.leaveScene();
        }
        // Create the scene if it doesn't exist
        this.findOrCreateScene(sceneIndex);
        // Preload the next one too
        if (this.config.scenes[sceneIndex + 1]) {
            this.findOrCreateScene(sceneIndex + 1);
        }
        // If nothing in the load queue, start audio now
        if (this._loadQueue.length === 0) {
            this.onSceneAudioReady();
        }
    }
    /**
     * Find a scene, or if it doesn't exist, create it
     * @param sceneIndex
     * @returns
     */
    findOrCreateScene(sceneIndex) {
        // scene already exists
        if (this.scenes[sceneIndex]) {
            return this.scenes[sceneIndex];
        }
        // Get the config for the scene
        const config = this.config.scenes[sceneIndex];
        // Don't do it if the scene doesn't exist
        if (!config) {
            // console.log("No scene config for", sceneIndex);
            return;
        }
        // Create a new scene if a config exists for it
        const audioScene = new AudioScene({
            config,
            id: sceneIndex,
            path: this.filePath,
        });
        this.scenes[sceneIndex] = audioScene;
        if (config.tracks) {
            // First check that any shared loops are loaded - load each one that isn't
            const sharedPlayersToLoad = config.tracks
                .filter(t => !!t.group)
                .map(track => this.sharedLoopsPlayers.get(track.group))
                .filter(player => !player.samplerExists);
            sharedPlayersToLoad.forEach(player => {
                const sampler = player.createSampler();
                sampler.connect(this.output);
                this.addToLoadQueue(player, player.groupId);
            });
            // Wait for the scene to be ready - if there are loops not included in the shared loops
            const sceneSpecificTracks = config.tracks.filter(track => !!!track.group);
            if (sceneSpecificTracks) {
                const sceneId = audioScene.name || `scene${sceneIndex}`;
                this.addToLoadQueue(audioScene, sceneId);
            }
        }
        else {
            // Turn down any shared loops
            this.sharedLoopsPlayers.forEach(player => player.exitScene(this.currentSceneData));
        }
        return audioScene;
    }
    addToLoadQueue(target, id) {
        if (!this._loadQueue.includes(id)) {
            target.addListener(AppEvent.LOAD_COMPLETE, () => this.registerLoadComplete(id));
            this._loadQueue.push(id);
        }
        console.log(`AudioWorld ${this.name} – addToLoadQueue`, this._loadQueue);
    }
    /**
     * Called when a scene has loaded all its audio
     * @param sceneId
     */
    registerLoadComplete(id) {
        if (this._loadQueue.includes(id)) {
            this._loadQueue = this._loadQueue.filter(name => name !== id);
        }
        console.log(`Register load complete ${id}`, this._loadQueue);
        if (this._loadQueue.length === 0) {
            this.onSceneAudioReady();
        }
    }
    /**
     * Called when all audio is ready to play
     */
    onSceneAudioReady() {
        this.log(`AudioWorld ${this.name} – onSceneAudioReady`);
        // Ramp up the volume if necessary
        if (!this._voiceoverPlaying) {
            if (this.output.gain.value < 0.85) {
                this.output.gain.rampTo(0.85, 3);
            }
        }
        // Do the audio crossfade
        this.crossfadeToScene(this._sceneIndex);
        // Start the transport, once per world
        // Maybe we can specify which scene on which to start the transport?
        const { tracks } = this.currentSceneData;
        const shouldStartTransport = Tone.Transport.state === "stopped";
        if ((tracks || this.sharedLoopsPlayers.entries.length > 0) && shouldStartTransport) {
            const elapsed = Tone.now() - this._playRequestedAt;
            console.log("Elapsed since request?", elapsed);
            const delay = Math.max(0, 1000 - elapsed);
            const offset = delay > 0 ? `+${delay / 1000}` : 0;
            console.log("Restarting transport - called from", this.name, "Delay offset was", offset);
            Tone.Transport.start(offset, "0:0:0"); // When I add 0 here the loops don't play
        }
        // Tell all the shared loops players to update their state
        this.sharedLoopsPlayers.forEach(player => player.enterScene(this.currentSceneData));
        if (this.bgPlayer) {
            const volume = this.currentSceneData.worldBackgroundVolume;
            console.log("Starting background player", this.bgPlayer.name, "with volume", volume);
            if (volume) {
                this.bgPlayer.enterScene(this.currentSceneData, volume);
            }
        }
    }
    /**
     * Crossfade between "a" and "b" sources in the mixer
     * @param sceneIndex
     */
    crossfadeToScene(sceneIndex) {
        const value = this._crossFadeIndex % 2 === 0 ? AudioSource.ONE : AudioSource.TWO;
        // Ensure the incoming scene is connected to the correct channel in the mixer
        const targetNode = this.crossFade[this._crossFadeIndex % 2 === 0 ? "a" : "b"];
        const incomingScene = this.scenes[sceneIndex];
        // Ensure the incoming scene is connected
        if (!incomingScene.connected) {
            incomingScene.connect(targetNode);
        }
        // move along
        this._crossFadeIndex++;
        incomingScene.enterScene();
        if (value === this.crossFade.fade.value) {
            return;
        }
        const duration = this.currentSceneData.fadeInDuration || 4;
        // This only manages the level of synced loops
        gsap.to(this.crossFade.fade, {
            value,
            duration,
            ease: Power2.easeInOut,
            onComplete: () => { var _a; return (_a = this.previousScene) === null || _a === void 0 ? void 0 : _a.disconnect(); },
        });
    }
    /**
    * Leave the world - recursively leave scenes, fade output gain to zero
    * Call cleanUp() when done
    */
    leave(stopTransportWhenDone = false) {
        // Leave all scenes
        this.scenes.forEach(scene => scene.leaveScene());
        // Turn down all shared loops
        this.sharedLoopsPlayers.forEach(player => player.exitScene());
        // Kill any voiceover
        this.stopVoiceover();
        // turn down the main out 
        gsap.to(this.output.gain, {
            value: 0,
            duration: 3,
            onComplete: () => {
                this.cleanUp();
                if (stopTransportWhenDone) {
                    Tone.Transport.stop();
                    Tone.Transport.position = "0:0:0";
                }
            },
            ease: Power2.easeInOut,
        });
    }
    /**
     * Clean up the audio graph once we have fully left this world
     */
    cleanUp() {
        console.log("Cleaning up audio world", this.name);
        this.scenes.forEach(scene => scene.cleanUp(true));
        // Stop any players that exist
        if (this.bgPlayer) {
            this.bgPlayer.stopAll();
            this.bgPlayer.disconnect();
        }
        this.sharedLoopsPlayers.forEach(player => {
            player.stopAll();
            player.disconnect();
        });
        // this._running = false;
    }
    /**
     *
     * @param step - the (optional) step to play
     * A convenience alias for playGestureAudio in the AudioPlayer class
     */
    playGestureAudio(step) {
        var _a;
        (_a = this.gesturePlayer) === null || _a === void 0 ? void 0 : _a.play(step);
    }
    /**
    * Reset the global transport settings & scene indices
    * - tempo
    * - time signature
    */
    resetGlobals() {
        // Reset scene indices
        this._sceneIndex = AudioStep.INIT;
        this._previousSceneIndex = AudioStep.INIT;
        this._crossFadeIndex = 0; // start again with this
        // Reset the transport
        if (this.config.global) {
            const { tempo, signature } = this.config.global;
            Tone.Transport.bpm.value = tempo;
            Tone.Transport.timeSignature = signature;
        }
    }
    // Getters
    get currentSceneData() {
        return this.config.scenes[this._sceneIndex];
    }
    get currentScene() {
        return this.scenes[this._sceneIndex];
    }
    get previousScene() {
        return this._previousSceneIndex > AudioStep.INIT ? this.scenes[this._previousSceneIndex] : null;
    }
    get noise() {
        return this.config.noise;
    }
    get filePath() {
        return this.config.path || this.name;
    }
    get sceneIndex() {
        return this._sceneIndex;
    }
    toString() {
        var _a, _b;
        return `
        ---------------------------------
        | AudioWorld: ${this.name}
        |--------------------------------
        | Scene Index: ${this._sceneIndex}
        | Current scene: ${(_a = this.currentScene) === null || _a === void 0 ? void 0 : _a.name}
        | Previous scene: ${(_b = this.previousScene) === null || _b === void 0 ? void 0 : _b.name}
        | Loop players: ${this.sharedLoopsPlayers ? "yes" : "no"}
        | Background player: ${this.bgPlayer ? "yes" : "no"}
        | Gesture player: ${this.gesturePlayer ? "yes" : "no"}
        --------------------------------`;
    }
}
